import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "briology_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (e) {
      console.error("CartContext: failed to load persisted cart", e);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error("CartContext: failed to persist cart", e);
    }
  }, [items, loaded]);

  // Functional setState — these callbacks are stable so consumers don't re-render.
  const add = useCallback((item) => {
    setItems((prev) => [
      ...prev,
      { ...item, qty: 1, id: `cart_${Date.now()}_${Math.random().toString(36).slice(2, 7)}` },
    ]);
  }, []);
  const setQty = useCallback((id, qty) => {
    const n = Math.max(1, Math.min(99, Number(qty) || 1));
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, qty: n } : x)));
  }, []);
  const inc = useCallback(
    (id) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, qty: Math.min(99, (x.qty || 1) + 1) } : x))),
    []
  );
  const dec = useCallback(
    (id) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, qty: Math.max(1, (x.qty || 1) - 1) } : x))),
    []
  );
  const remove = useCallback((id) => setItems((prev) => prev.filter((x) => x.id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(
    () => items.reduce((s, x) => s + (x.qty || 1), 0),
    [items]
  );
  const subtotal = useMemo(
    () => items.reduce((s, x) => s + (Number(x.price) || 0) * (x.qty || 1), 0),
    [items]
  );

  const value = useMemo(
    () => ({ items, add, remove, setQty, inc, dec, clear, count, subtotal }),
    [items, add, remove, setQty, inc, dec, clear, count, subtotal]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
